import React from "react";
import styled from "styled-components";
import BusinessUnitsTab from "./BusinessUnitsTab";
import OrganizationHeader from "./OrganizationHeader";
import Page from "components/UI/Page";
import TabsComponent from "components/UI/Tabs";
import { ORGANIZATION_TABS, ORGANIZATION_TABS_WITH_ADMINS } from "constants/tabs";
import { ORGANIZATION_CONSTANTS } from "constants/organization";
import Vendor from "data/Vendor";
import User, { hasOfmpAdminProfile, isOfmpAdmin } from "data/User";
import { Background, Main } from "styles/themeColors";
import Modal from "components/UI/Modal";
import AboutBusinessUnit from "./AboutBusinessUnit";
import { useBusinessUnitForm } from "hooks/useBusinessUnitForm";
import { GENERAL_CONSTANTS } from "constants/general";
import LoadingIndicator from "components/UI/LoadingIndicator";
import AllUsersTab from "./AllUsersTab";
import AdminsTab from "./AdminsTab";
import BusinessUnitUserInviteModal from "./BusinessUnitUserInviteModal";
import { BusinessUnit } from "data/BusinessUnit";
import { useUserInvitation } from "hooks/useUserInvitation";
import FeatureFlag from "data/FeatureFlag";

// Module-level variable to preserve activeTab across component remounts
// This persists during business unit changes but resets on page refresh
let preservedActiveTab: number | null = null;

const EmptyMessage = styled.span`
  font-size: 24px;
  padding: 24px 0;
`;

interface Props {
    selectedVendor?: Vendor;
    user: User;
    selectedBusinessUnit: BusinessUnit;
    featureFlags?: FeatureFlag;
    vendorsList?: Vendor[];
};

const BusinessUnitsView = (props: Props) => {

    const { selectedVendor, user, selectedBusinessUnit, featureFlags, vendorsList = [] } = props;

    // Determine if user is OFMP admin and if admin view is enabled
    const isUserOfmpAdmin = hasOfmpAdminProfile(user) || isOfmpAdmin(user);
    // Initialize from preserved value if exists (for business unit changes),
    // otherwise default to 0 (when coming from other pages)
    const [activeTab, setActiveTab] = React.useState<number>(() => {
        return preservedActiveTab !== null ? preservedActiveTab : 0;
    });

    const isAdminViewEnabled = React.useMemo(() => {
        if (!selectedBusinessUnit && !selectedVendor && isUserOfmpAdmin) return true;
        return false;
    }, [isUserOfmpAdmin, selectedBusinessUnit, selectedVendor]);

    // Determine which tabs to show based on user role
    const organizationTabs = React.useMemo(() => {
        if (isUserOfmpAdmin) {
            // For OFMP admins, show tabs with Admins tab
            // If admin view is disabled, mark Admins tab as disabled
            return ORGANIZATION_TABS_WITH_ADMINS.map((tab, index) => {
                if (index === ORGANIZATION_CONSTANTS.ADMINS_TAB_INDEX && !isAdminViewEnabled) {
                    return { ...tab, disabled: true };
                }
                return tab;
            });
        }
        return ORGANIZATION_TABS;
    }, [isUserOfmpAdmin, isAdminViewEnabled]);

    // Reset active tab when Admins tab becomes disabled
    React.useEffect(() => {
        if (activeTab === ORGANIZATION_CONSTANTS.ADMINS_TAB_INDEX && !isAdminViewEnabled && isUserOfmpAdmin) {
            setActiveTab(0);
        }
    }, [isAdminViewEnabled, activeTab, isUserOfmpAdmin]);


    // Save activeTab to module variable whenever it changes
    React.useEffect(() => {
        preservedActiveTab = activeTab;
    }, [activeTab]);

    // Clear preserved tab when unmounting (navigating away from organizations)
    React.useEffect(() => {
        return () => {
            preservedActiveTab = null;
        };
    }, []);

    const {
        control,
        handleSubmit,
        watch,
        errors,
        onSubmit,
        setError,
        clearErrors,
        vendorBrands,
        trigger,
        reloadListing: reload,
        setReloadListing: setReload,
        isLoading,
        reset,
        setShowCreateModal,
        showCreateModal
    } = useBusinessUnitForm(selectedVendor!, undefined, featureFlags);  // Using the custom hook

    // Determine the view mode based on active tab
    const inviteViewMode = React.useMemo(() => {
        if (activeTab === ORGANIZATION_CONSTANTS.ADMINS_TAB_INDEX && isUserOfmpAdmin) {
            return GENERAL_CONSTANTS.ADMINS;
        }
        return GENERAL_CONSTANTS.ALL_USERS;
    }, [activeTab, isUserOfmpAdmin]);

    // User invitation logic using custom hook
    const {
        showInviteModal,
        userProfileId,
        inviteForm,
        inviteErrors,
        alreadyInvitedRole,
        isLoading: inviteLoading,
        toggleInviteModal,
        handleInviteSend,
        handleInviteInputChange,
        setUserProfileId,
        setAlreadyInvitedRole,
        handleFormReset
    } = useUserInvitation({
        businessUnitId: selectedBusinessUnit?.id?.toString(),
        vendorId: selectedVendor?.id?.toString(),
        onSuccess: () => setReload(true),
        viewMode: inviteViewMode
    });

    // Reset invite form when switching between tabs
    React.useEffect(() => {
        handleFormReset();
    }, [activeTab]);

    const handleTabChange = (index: number) => {
        setActiveTab(index);
    };

    const createButtonClick = () => {
        // Handle create button click
        setShowCreateModal(true);
    };

    const onSave = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        const isFormValid = await trigger(); // Validate all fields
        if (!isFormValid) return;

        await handleSubmit(async (data) => {
            await onSubmit({ ...data, vendorId: selectedVendor?.id })
        })();
    }

    const onClose = () => {
        reset();
        setShowCreateModal(false);
    };

    if (isLoading) return <LoadingIndicator center />

    return (
        <Page bgColor={Background.white}>
            <Modal
                showModal={showCreateModal}
                onClose={onClose}
                showCloseIcon
                onClick={onSave}
                title={ORGANIZATION_CONSTANTS.CREATE_BUSINESS_UNIT}
                primaryButtonText={GENERAL_CONSTANTS.SAVE}
                width="600px"
                primaryButtonColor={Main.brandDark}
                color={Main.black}
                borderColor={Main.lightGray}
                ghostButtonText={GENERAL_CONSTANTS.BACK}
                left="35%"
            >
                <AboutBusinessUnit
                    control={control}
                    brands={vendorBrands!}
                    errors={errors}
                    watch={watch}
                    setError={setError}
                    clearErrors={clearErrors}
                    width="100%"
                />
            </Modal>
            <OrganizationHeader
                user={user}
                onCreate={createButtonClick}
                disabled={!selectedVendor}
                activeTab={activeTab}
                toggleInviteModal={toggleInviteModal}
                isAdminViewEnabled={isAdminViewEnabled}
            />

            <TabsComponent tabs={organizationTabs}
                activeTab={activeTab}
                setActiveTab={handleTabChange}
                theme='brandDark' borderRadius='20px' margin='8px 24px 8px 0' height='auto' className="brands-tabs"
                disabledTabTooltip="">

                {!selectedVendor ? (
                    <EmptyMessage>{ORGANIZATION_CONSTANTS.VENDOR_SELECT_WARNING.replace('{{entity}}', 'business units')}</EmptyMessage>
                ) : (
                    <BusinessUnitsTab
                        selectedVendor={selectedVendor}
                        selectedBusinessUnit={selectedBusinessUnit}
                        reload={reload}
                        setReload={setReload}
                    />
                )}

                {!selectedVendor ? (
                    <EmptyMessage>{ORGANIZATION_CONSTANTS.VENDOR_SELECT_WARNING.replace('{{entity}}', 'users')}</EmptyMessage>
                ) : (
                    <AllUsersTab selectedVendor={selectedVendor}
                        selectedBusinessUnit={selectedBusinessUnit}
                        toggleInviteModal={toggleInviteModal}
                        setUserProfileId={setUserProfileId}
                        handleInviteInputChange={handleInviteInputChange}
                        reload={reload}
                        setReload={setReload}
                    />
                )}

                {/* Admins Tab - Only visible to OFMP admins */}
                {isUserOfmpAdmin && (
                    <AdminsTab
                        toggleInviteModal={toggleInviteModal}
                        setUserProfileId={setUserProfileId}
                        handleInviteInputChange={handleInviteInputChange}
                        reload={reload}
                        setReload={setReload}
                        vendorsList={vendorsList}
                        actionsDisabled={!isAdminViewEnabled}
                    />
                )}

            </TabsComponent>

            {showInviteModal && <BusinessUnitUserInviteModal
                show={showInviteModal}
                onClose={toggleInviteModal}
                onSendInvitation={handleInviteSend}
                inviteForm={inviteForm}
                onInputChange={handleInviteInputChange}
                errors={inviteErrors}
                isEdit={!!userProfileId}
                alreadyInvitedRole={alreadyInvitedRole}
                setAlreadyInvitedRole={setAlreadyInvitedRole}
                isLoading={inviteLoading}
                viewMode={inviteViewMode}
                selectedVendor={selectedVendor}
                selectedBusinessUnit={selectedBusinessUnit}
                salesOrderFeatureFlag={featureFlags?.salesOrder?.enabled!}
            />
            }

        </Page>
    );
}

export default BusinessUnitsView;